import { useMemo } from "react";
import styled from "styled-components";
import Emojify from "app/components/shared/Emojify";
import CopyTextButton from "app/components/analytics/shared/CopyTextButton";
import { _trackTALinkClicked } from "app/components/analytics/shared/segmentEvents";

export type MetadataCardType = {
  run_name: string;
  run_url: string;
  test_name: string;
  test_url: string;
  location: string | null | undefined;
  tags: {
    key_value: string;
  }[];
};

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0 20px;
  border-radius: 3px;
  background: var(--base-0);

  div {
    border-bottom: 1px solid #e0e0e0;
  }

  div:last-child {
    border: 0;
  }
`;

const StyledRow = styled.div`
  display: grid;
  grid-template-columns: 100px auto auto;
  align-items: baseline;
  word-break: break-all;
  padding: 15px 0px;
  width: 100%;
`;

const MetadataCard = ({
  run_url,
  run_name,
  test_name,
  test_url,
  location,
  tags,
}: MetadataCardType) => {
  const baseUrl = new URL(window.location.href);
  const activeTags = baseUrl.searchParams.get("tags")?.split(",") || [];

  const generateTagUrl = (url, tags) => {
    const updatedTags = Array.from(new Set([...activeTags, ...tags]));
    // Remove the execution params so the drawer does not open
    url.searchParams.delete("execution_id");
    url.searchParams.delete("run_id");

    // Add the updated tags
    url.searchParams.set("tags", updatedTags.join(","));

    return url.toString();
  };

  const tagsWithUrls = useMemo(() => {
    if (tags.length === 0) {
      return [];
    }

    return tags.map(({ key_value }) => {
      const url = new URL(window.location.href);
      const tagUrl = generateTagUrl(url, [key_value]);

      return {
        key_value,
        url: tagUrl,
      };
    });
  }, [tags]);

  return (
    <HeaderContainer>
      <StyledRow>
        <span className="mr4 bold">Run</span>
        <span className="truncate">
          <a
            href={run_url}
            title={run_name}
            className="lime"
            onClick={() =>
              _trackTALinkClicked({
                linkTo: "run#show",
                linkTitle: run_name,
                url: run_url,
                source: "MetadataCard link",
              })
            }
          >
            <Emojify text={run_name} />
          </a>
        </span>
      </StyledRow>

      <StyledRow>
        <span className="mr4 bold">Test</span>
        <span className="truncate">
          <a
            href={test_url}
            title={test_name}
            className="lime"
            onClick={() =>
              _trackTALinkClicked({
                linkTo: "test#show",
                linkTitle: test_name,
                url: test_url,
                source: "MetadataCard link",
              })
            }
          >
            {test_name}
          </a>
        </span>
      </StyledRow>

      {location && (
        <StyledRow>
          <span className="mr4 bold">Location</span>
          <CopyTextButton text={location} className="monospace charcoal-700" />
        </StyledRow>
      )}

      {window.Features.AnalyticsShowExecutionTags &&
        tagsWithUrls.length > 0 && (
          <StyledRow>
            <span className="mr4 bold">Tags</span>
            <div className="flex flex-wrap items-center gap0.5 overflow-hidden w-full">
              {tagsWithUrls.map((tag) => (
                <a
                  key={tag.key_value}
                  href={tag.url}
                  className={`badge badge--outline truncate${activeTags.includes(tag.key_value) ? " bg-gray-400" : ""}`}
                >
                  {tag.key_value}
                </a>
              ))}
            </div>
          </StyledRow>
        )}
    </HeaderContainer>
  );
};

export default MetadataCard;
